// Serviço para gerenciar clientes no Supabase
import { supabase } from '../lib/supabase';
import type { Cliente, Endereco } from '../types';

// Interface para cliente no banco de dados
interface ClienteDB {
  id: string;
  nome: string;
  tipo: 'F' | 'J';
  cpf?: string;
  cnpj?: string;
  email: string;
  telefone: string;
  endereco_id: string;
  created_at: string;
  updated_at: string;
}

// Converter cliente do banco para o formato da aplicação
function clienteDBToCliente(clienteDB: ClienteDB, endereco: Endereco): Cliente {
  return {
    id: clienteDB.id,
    nome: clienteDB.nome,
    tipo: clienteDB.tipo,
    cpf: clienteDB.cpf || undefined,
    cnpj: clienteDB.cnpj || undefined,
    email: clienteDB.email,
    telefone: clienteDB.telefone,
    endereco,
    createdAt: clienteDB.created_at,
    updatedAt: clienteDB.updated_at,
  };
}

// Converter cliente da aplicação para o formato do banco
function clienteToClienteDB(cliente: Partial<Cliente>): Partial<ClienteDB> {
  return {
    nome: cliente.nome,
    tipo: cliente.tipo,
    cpf: cliente.cpf || null,
    cnpj: cliente.cnpj || null,
    email: cliente.email,
    telefone: cliente.telefone,
  };
}

// Buscar todos os clientes da empresa atual
export async function getClientes(empresaId: string): Promise<Cliente[]> {
  try {
    // Buscar clientes sem join para evitar recursão RLS
    const { data: clientes, error: clientesError } = await supabase
      .from('clientes')
      .select('id, nome, tipo, cpf, cnpj, email, telefone, endereco_id, created_at, updated_at')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });

    if (clientesError) {
      console.error('[clientesService] Erro ao buscar clientes:', clientesError);
      // Retornar array vazio em vez de quebrar
      return [];
    }

    if (!clientes || clientes.length === 0) {
      return [];
    }

    // Buscar endereços separadamente para cada cliente
    const clientesComEnderecos = await Promise.all(
      clientes.map(async (c: any) => {
        let endereco: Endereco = {
          cep: '',
          logradouro: '',
          numero: '',
          bairro: '',
          cidade: '',
          estado: '',
        };

        if (c.endereco_id) {
          try {
            const { data: enderecoData, error: enderecoError } = await supabase
              .from('enderecos')
              .select('cep, logradouro, numero, complemento, bairro, cidade, estado')
              .eq('id', c.endereco_id)
              .single();

            if (!enderecoError && enderecoData) {
              endereco = {
                cep: enderecoData.cep || '',
                logradouro: enderecoData.logradouro || '',
                numero: enderecoData.numero || '',
                complemento: enderecoData.complemento || undefined,
                bairro: enderecoData.bairro || '',
                cidade: enderecoData.cidade || '',
                estado: enderecoData.estado || '',
              };
            }
          } catch (err) {
            console.warn('[clientesService] Erro ao buscar endereço para cliente:', c.id, err);
            // Continuar com endereço vazio
          }
        }

        return clienteDBToCliente(c, endereco);
      })
    );

    return clientesComEnderecos;
  } catch (error) {
    console.error('[clientesService] Erro geral ao buscar clientes:', error);
    // Retornar array vazio em vez de quebrar
    return [];
  }
}

// Buscar cliente por ID
export async function getClienteById(id: string): Promise<Cliente | null> {
  try {
    const { data: cliente, error: clienteError } = await supabase
      .from('clientes')
      .select('*, enderecos(*)')
      .eq('id', id)
      .single();

    if (clienteError) throw clienteError;
    if (!cliente) return null;

    const endereco: Endereco = {
      cep: cliente.enderecos?.cep || '',
      logradouro: cliente.enderecos?.logradouro || '',
      numero: cliente.enderecos?.numero || '',
      complemento: cliente.enderecos?.complemento || undefined,
      bairro: cliente.enderecos?.bairro || '',
      cidade: cliente.enderecos?.cidade || '',
      estado: cliente.enderecos?.estado || '',
    };

    return clienteDBToCliente(cliente, endereco);
  } catch (error) {
    console.error('Erro ao buscar cliente:', error);
    throw error;
  }
}

// Criar novo cliente
export async function createCliente(
  cliente: Omit<Cliente, 'id' | 'createdAt' | 'updatedAt'>,
  contaId: string,
  empresaId: string
): Promise<Cliente> {
  try {
    // Primeiro, criar o endereço
    const { data: endereco, error: enderecoError } = await supabase
      .from('enderecos')
      .insert({
        cep: cliente.endereco.cep,
        logradouro: cliente.endereco.logradouro,
        numero: cliente.endereco.numero,
        complemento: cliente.endereco.complemento || null,
        bairro: cliente.endereco.bairro,
        cidade: cliente.endereco.cidade,
        estado: cliente.endereco.estado,
      })
      .select()
      .single();

    if (enderecoError) throw enderecoError;

    // Depois, criar o cliente
    const { data: novoCliente, error: clienteError } = await supabase
      .from('clientes')
      .insert({
        nome: cliente.nome,
        tipo: cliente.tipo,
        cpf: cliente.cpf || null,
        cnpj: cliente.cnpj || null,
        email: cliente.email,
        telefone: cliente.telefone,
        endereco_id: endereco.id,
        conta_id: contaId,
        empresa_id: empresaId,
      })
      .select()
      .single();

    if (clienteError) throw clienteError;

    return clienteDBToCliente(novoCliente, cliente.endereco);
  } catch (error) {
    console.error('Erro ao criar cliente:', error);
    throw error;
  }
}

// Atualizar cliente
export async function updateCliente(id: string, cliente: Partial<Cliente>): Promise<Cliente> {
  try {
    // Buscar cliente atual para obter endereco_id
    const { data: clienteAtual, error: buscaError } = await supabase
      .from('clientes')
      .select('endereco_id, enderecos(*)')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Atualizar endereço se fornecido
    if (cliente.endereco && clienteAtual.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .update({
          cep: cliente.endereco.cep,
          logradouro: cliente.endereco.logradouro,
          numero: cliente.endereco.numero,
          complemento: cliente.endereco.complemento || null,
          bairro: cliente.endereco.bairro,
          cidade: cliente.endereco.cidade,
          estado: cliente.endereco.estado,
        })
        .eq('id', clienteAtual.endereco_id);

      if (enderecoError) throw enderecoError;
    }

    // Atualizar cliente
    const clienteData = clienteToClienteDB(cliente);
    const { data: clienteAtualizado, error: clienteError } = await supabase
      .from('clientes')
      .update(clienteData)
      .eq('id', id)
      .select('*, enderecos(*)')
      .single();

    if (clienteError) throw clienteError;

    const endereco: Endereco = {
      cep: clienteAtualizado.enderecos?.cep || '',
      logradouro: clienteAtualizado.enderecos?.logradouro || '',
      numero: clienteAtualizado.enderecos?.numero || '',
      complemento: clienteAtualizado.enderecos?.complemento || undefined,
      bairro: clienteAtualizado.enderecos?.bairro || '',
      cidade: clienteAtualizado.enderecos?.cidade || '',
      estado: clienteAtualizado.enderecos?.estado || '',
    };

    return clienteDBToCliente(clienteAtualizado, endereco);
  } catch (error) {
    console.error('Erro ao atualizar cliente:', error);
    throw error;
  }
}

// Deletar cliente
export async function deleteCliente(id: string): Promise<void> {
  try {
    // Buscar cliente para obter endereco_id
    const { data: cliente, error: buscaError } = await supabase
      .from('clientes')
      .select('endereco_id')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Deletar cliente
    const { error: clienteError } = await supabase
      .from('clientes')
      .delete()
      .eq('id', id);

    if (clienteError) throw clienteError;

    // Deletar endereço se não estiver sendo usado por outros registros
    if (cliente.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .delete()
        .eq('id', cliente.endereco_id);

      // Não lançar erro se o endereço estiver sendo usado por outros registros
      if (enderecoError && !enderecoError.message.includes('violates foreign key')) {
        console.warn('Erro ao deletar endereço:', enderecoError);
      }
    }
  } catch (error) {
    console.error('Erro ao deletar cliente:', error);
    throw error;
  }
}

// Buscar clientes por termo (para busca global)
export async function searchClientes(term: string, empresaId: string): Promise<Cliente[]> {
  try {
    const termLower = term.toLowerCase();
    
    const { data: clientes, error } = await supabase
      .from('clientes')
      .select('*, enderecos(*)')
      .eq('empresa_id', empresaId)
      .or(`nome.ilike.%${termLower}%,cpf.ilike.%${term}%,cnpj.ilike.%${term}%,email.ilike.%${termLower}%`)
      .limit(20);

    if (error) throw error;

    return (clientes || []).map((c: any) => {
      const endereco: Endereco = {
        cep: c.enderecos?.cep || '',
        logradouro: c.enderecos?.logradouro || '',
        numero: c.enderecos?.numero || '',
        complemento: c.enderecos?.complemento || undefined,
        bairro: c.enderecos?.bairro || '',
        cidade: c.enderecos?.cidade || '',
        estado: c.enderecos?.estado || '',
      };

      return clienteDBToCliente(c, endereco);
    });
  } catch (error) {
    console.error('Erro ao buscar clientes:', error);
    throw error;
  }
}

